'use client';

import type { ReactElement } from 'react';
import {
  AestheticRows,
  AvatarRows,
  MemberRows,
  ProductRows,
  ProjectRows,
  ThreadRows,
} from '../sections';
import type {
  AestheticResult,
  AvatarResult,
  MemberResult,
  OrgThreadResult,
  ProductResult,
  ProjectResult,
  SearchScope,
} from '../types';

export type ScopePageText = {
  products: string;
  threads: string;
  recentProducts: string;
  recentThreads: string;
  projects: string;
  avatars: string;
  aesthetics: string;
  members: string;
  typeToSearch: string;
};

export function ScopePage({
  scope,
  text,
  shouldSearch,
  products,
  threads,
  projects,
  avatars,
  aesthetics,
  members,
  roleLabels,
  formatTime,
  onSelectProduct,
  onSelectThread,
  onSelectProject,
  onSelectAvatar,
  onSelectAesthetic,
  onSelectMember,
}: {
  scope: SearchScope;
  text: ScopePageText;
  shouldSearch: boolean;
  products: ProductResult[];
  threads: OrgThreadResult[];
  projects: ProjectResult[];
  avatars: AvatarResult[];
  aesthetics: AestheticResult[];
  members: MemberResult[];
  roleLabels: Record<'owner' | 'admin' | 'member', string>;
  formatTime: (timestamp: number) => string;
  onSelectProduct: (product: ProductResult) => void;
  onSelectThread: (thread: OrgThreadResult) => void;
  onSelectProject: (project: ProjectResult) => void;
  onSelectAvatar: (avatar: AvatarResult) => void;
  onSelectAesthetic: (aesthetic: AestheticResult) => void;
  onSelectMember: (member: MemberResult) => void;
}): ReactElement {
  switch (scope) {
    case 'products':
      return (
        <ProductRows
          products={products}
          heading={shouldSearch ? text.products : text.recentProducts}
          formatTime={formatTime}
          onSelect={onSelectProduct}
        />
      );
    case 'threads':
      return (
        <ThreadRows
          threads={threads}
          heading={shouldSearch ? text.threads : text.recentThreads}
          formatTime={formatTime}
          onSelect={onSelectThread}
        />
      );
    case 'projects':
      return (
        <ProjectRows
          projects={projects}
          heading={text.projects}
          formatTime={formatTime}
          onSelect={onSelectProject}
        />
      );
    case 'avatars':
      return avatars.length === 0 && !shouldSearch ? (
        <TypeToSearchHint label={text.typeToSearch} />
      ) : (
        <AvatarRows
          avatars={avatars}
          heading={text.avatars}
          onSelect={onSelectAvatar}
        />
      );
    case 'aesthetics':
      return aesthetics.length === 0 && !shouldSearch ? (
        <TypeToSearchHint label={text.typeToSearch} />
      ) : (
        <AestheticRows
          aesthetics={aesthetics}
          heading={text.aesthetics}
          onSelect={onSelectAesthetic}
        />
      );
    case 'members':
      return (
        <MemberRows
          members={members}
          heading={text.members}
          roleLabels={roleLabels}
          onSelect={onSelectMember}
        />
      );
    default: {
      const exhaustive: never = scope;
      throw new Error(`Unhandled command menu scope: ${exhaustive}`);
    }
  }
}

const TypeToSearchHint = ({ label }: { label: string }): ReactElement => {
  return (
    <div className="text-muted-foreground/60 px-3 py-6 text-center text-sm">
      {label}
    </div>
  );
};
